import useRealtimeReadOnlyList from '../../realtime/useRealtimeReadOnlyList';
import React, { useEffect, useRef, useState } from 'react';
import { Collapse, Alert, Button, Form, Modal } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { usePostDataMutation, useDeleteDataMutation } from '../../redux/api/portalApi';
import { selectApiUrls } from '../../redux/slices/apiUrlSlice';
import { SHIPPING_COLUMNS, shippingRecords } from './shippingConfig';
import { defaultShippingView, normalizeShippingView, SHIPPING_VIEW_CATEGORY } from './shippingViews';
import ShippingSaveBanner from './ShippingSaveBanner';

export default function ShippingViewControls({ config, onChange, disabled }) {
  const urls = useSelector(selectApiUrls);
  const userId = useSelector((state) => state.user.userDetails?.localAccountId);
  const preferences = useRealtimeReadOnlyList({ url: userId ? urls.userPreferences : undefined });
  const [post] = usePostDataMutation();
  const [remove] = useDeleteDataMutation();
  const [dialog, setDialog] = useState(null);
  const [columnsOpen, setColumnsOpen] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState('');
  const [working, setWorking] = useState(false);
  const [notification, setNotification] = useState(null);
  const initialized = useRef(null);
  const applyRef = useRef(onChange);
  applyRef.current = onChange;
  const views = shippingRecords(preferences.data).filter((item) => item.Category === SHIPPING_VIEW_CATEGORY && String(item.UserObjectID) === String(userId));
  useEffect(() => {
    if (!userId || !preferences.data || preferences.isFetching || disabled || initialized.current === userId) return;
    initialized.current = userId;
    const initial = shippingRecords(preferences.data).find((item) => item.Category === SHIPPING_VIEW_CATEGORY && String(item.UserObjectID) === String(userId) && ['default', 'default view'].includes(String(item.Key).toLowerCase()));
    if (initial) {
      try { applyRef.current(normalizeShippingView(JSON.parse(initial.Value))); }
      catch { setError('The default view could not be loaded.'); }
    }
  }, [preferences.data, preferences.isFetching, userId, disabled]);
  const save = async () => {
    setWorking(true); setError('');
    setNotification({ message: 'Saving view…', pending: true });
    try {
      await post({ url: urls.userPreferences, data: { userObjectID: userId, category: SHIPPING_VIEW_CATEGORY, key: name.trim(), value: JSON.stringify({ ...config, description }) } }).unwrap();
      setDialog(null); setName(''); setDescription('');
      const result = await preferences.refetch();
      setNotification({ failed: Boolean(result?.error), message: result?.error ? 'View saved. Failed to refresh saved views.' : 'View saved.' });
    } catch { setError('Could not save the view. Please retry.'); setNotification({ failed: true, message: 'Failed to save the view. Please retry.' }); }
    finally { setWorking(false); }
  };
  const deleteView = async (view) => {
    setWorking(true); setError('');
    setNotification({ message: 'Deleting view…', pending: true });
    try {
      await remove({ url: urls.userPreferences, data: { ID: view.ID ?? view.Id } }).unwrap();
      const result = await preferences.refetch();
      setNotification({ failed: Boolean(result?.error), message: result?.error ? 'View deleted. Failed to refresh saved views.' : 'View deleted.' });
    } catch { setError('Could not delete the view. Please retry.'); setNotification({ failed: true, message: 'Failed to delete the view. Please retry.' }); }
    finally { setWorking(false); }
  };
  return <div className="mb-3">
    <ShippingSaveBanner notification={notification} />
    {(error || preferences.error) && <Alert variant="danger">{error || 'Saved views could not be loaded.'}</Alert>}
    <fieldset disabled={disabled || working}>
      <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
        <Button variant="outline-success" disabled={!userId || !urls.userPreferences} onClick={() => setDialog('save')}>Save View</Button>
        <Button variant="outline-primary" disabled={!userId || !urls.userPreferences} onClick={() => setDialog('load')}>Load View</Button>
        <Button variant="outline-secondary" onClick={() => onChange(defaultShippingView())}>Reset View</Button>
        <Button variant="outline-secondary" aria-expanded={columnsOpen} aria-controls="shipping-column-options" onClick={() => setColumnsOpen(!columnsOpen)}>Columns</Button>
      </div>
      <Collapse in={columnsOpen}><div id="shipping-column-options">
        <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
          <strong>Select Columns to Display</strong><span className="text-muted">{config.visibleColumns.length} selected</span>
          <Button size="sm" variant="outline-success" onClick={() => onChange({ ...config, visibleColumns: SHIPPING_COLUMNS['data-entry'].map(({ key }) => key) })}>Show all columns</Button>
          <Button size="sm" variant="outline-secondary" onClick={() => onChange({ ...config, visibleColumns: ['JobNumber'] })}>Show only Job Number</Button>
          <Button size="sm" variant="outline-secondary" onClick={() => onChange({ ...config, visibleColumns: defaultShippingView().visibleColumns })}>Reset columns</Button>
        </div>
        <div className="d-flex flex-wrap gap-2">{SHIPPING_COLUMNS['data-entry'].map(({ key, label }) => {
          const selected = config.visibleColumns.includes(key);
          return <Button key={key} size="sm" className="rounded-pill" variant={selected ? 'primary' : 'outline-secondary'} aria-label={`Show ${label}`} aria-pressed={selected} disabled={selected && config.visibleColumns.length === 1} onClick={() => onChange({ ...config, visibleColumns: selected ? config.visibleColumns.filter((item) => item !== key) : [...config.visibleColumns, key] })}>
            {selected && <i className="bi bi-check2 me-1" aria-hidden="true" />}{label}
          </Button>;
        })}</div>
      </div></Collapse>
    </fieldset>
    <Modal show={dialog !== null} onHide={() => !working && setDialog(null)}>
      <Modal.Header closeButton={!working}><Modal.Title>{dialog === 'save' ? 'Save Filter View' : 'Load Saved View'}</Modal.Title></Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        {dialog === 'save' ? <>
          <Form.Group controlId="shipping-view-name"><Form.Label>View Name</Form.Label><Form.Control value={name} onChange={(event) => setName(event.target.value)} /></Form.Group>
          <Form.Group controlId="shipping-view-description"><Form.Label>Description (Optional)</Form.Label><Form.Control as="textarea" value={description} onChange={(event) => setDescription(event.target.value)} /></Form.Group>
          <Form.Text>Name a view Default to load it automatically when opening Data Entry.</Form.Text>
        </> : views.length ? views.map((view) => <div key={view.ID ?? view.Id} className="d-flex justify-content-between gap-2 border-bottom py-2">
          <span>{view.Key}</span><div className="d-flex gap-2">
            <Button size="sm" disabled={disabled || working} onClick={() => { try { onChange(normalizeShippingView(JSON.parse(view.Value))); setDialog(null); setError(''); } catch { setError('This saved view is invalid.'); } }}>Load {view.Key}</Button>
            <Button size="sm" variant="outline-danger" disabled={working || (view.ID ?? view.Id) == null} onClick={() => deleteView(view)}>Delete {view.Key}</Button>
          </div>
        </div>) : <p>No saved views found.</p>}
      </Modal.Body>
      <Modal.Footer>{dialog === 'save' && <Button disabled={working || disabled || !name.trim()} onClick={save}>Save View</Button>}<Button variant="secondary" disabled={working} onClick={() => setDialog(null)}>Close</Button></Modal.Footer>
    </Modal>
  </div>;
}
